import React, { useEffect, useState } from 'react';
import CrownFrameBadge, { CARD_RANKS } from './CrownFrameBadge';

/**
 * RankPromotionModal — shows a celebration when a player reaches a new card rank.
 * Card rank promotions get a full-screen modal with animation.
 * Suit level promotions get a brief toast notification.
 */
export default function RankPromotionModal({ promotion, onClose }) {
  const [visible, setVisible] = useState(false);
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (promotion) {
      setVisible(true);
      // Generate celebration particles
      const p = Array.from({ length: 30 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        delay: Math.random() * 1.5,
        duration: 2 + Math.random() * 2,
        size: 4 + Math.random() * 8,
        color: ['#FFD700', '#D4AF37', '#FFF8DC', '#FF6B6B', '#60A5FA', '#6EE7B7'][Math.floor(Math.random() * 6)],
      }));
      setParticles(p);
      // Auto-close after 5 seconds
      const timer = setTimeout(() => {
        setVisible(false);
        setTimeout(onClose, 500);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [promotion, onClose]);

  if (!promotion) return null;

  const rank = CARD_RANKS.find(r => r.id === promotion.newCardRank) || CARD_RANKS[0];
  const isBower = promotion.newCardRank >= 7;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 10000,
      opacity: visible ? 1 : 0,
      transition: 'opacity 0.5s ease',
      pointerEvents: visible ? 'auto' : 'none',
    }} onClick={() => { setVisible(false); setTimeout(onClose, 500); }}>
      {/* Celebration particles */}
      {particles.map(p => (
        <div key={p.id} style={{
          position: 'absolute',
          left: `${p.x}%`,
          top: '-10px',
          width: p.size,
          height: p.size,
          background: p.color,
          borderRadius: '50%',
          animation: `rankParticleFall ${p.duration}s ease-in ${p.delay}s forwards`,
        }} />
      ))}

      <div style={{
        textAlign: 'center',
        animation: 'rankPromotionScale 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards',
      }}>
        {/* Title */}
        <div style={{
          color: '#FFD700',
          fontSize: isBower ? 28 : 22,
          fontWeight: 700,
          fontFamily: "'Georgia', serif",
          letterSpacing: 2,
          marginBottom: 24,
          textShadow: '0 0 20px rgba(255,215,0,0.5)',
        }}>
          {isBower ? 'LEGENDARY ACHIEVEMENT' : 'RANK PROMOTION'}
        </div>

        {/* Badge display */}
        <div style={{
          transform: 'scale(1.5)',
          marginBottom: 32,
          filter: isBower ? 'drop-shadow(0 0 20px rgba(255,215,0,0.6))' : 'none',
        }}>
          <CrownFrameBadge
            cardRankId={promotion.newCardRank}
            suitSymbol="♠"
            showPoints={false}
            size="lg"
          />
        </div>

        {/* Rank name */}
        <div style={{
          color: '#FFFFFF',
          fontSize: isBower ? 32 : 26,
          fontWeight: 700,
          fontFamily: "'Georgia', serif",
          marginBottom: 12,
          letterSpacing: 1,
        }}>
          {rank.name}
        </div>

        <div style={{
          color: '#8b949e',
          fontSize: 16,
          fontFamily: "'Georgia', serif",
          marginBottom: 24,
        }}>
          {promotion.message || `You have reached ${rank.name} status!`}
        </div>

        <div style={{
          color: '#484f58',
          fontSize: 13,
        }}>
          Tap anywhere to continue
        </div>
      </div>

      {/* CSS animations */}
      <style>{`
        @keyframes rankPromotionScale {
          0% { transform: scale(0.3); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes rankParticleFall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

/**
 * SuitPromotionToast — brief notification for suit level changes
 */
export function SuitPromotionToast({ suitName, suitSymbol, onClose }) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(onClose, 300);
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const suitColors = { '♣': '#1A5C34', '♥': '#C62828', '♠': '#1A1A1A', '♦': '#D4AF37' };

  return (
    <div style={{
      position: 'fixed', top: 20, right: 20,
      background: '#161b22',
      border: `2px solid ${suitColors[suitSymbol] || '#30363d'}`,
      borderRadius: 12,
      padding: '16px 24px',
      zIndex: 10000,
      display: 'flex', alignItems: 'center', gap: 12,
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateX(0)' : 'translateX(100%)',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    }}>
      <span style={{ fontSize: 28 }}>{suitSymbol}</span>
      <div>
        <div style={{ color: '#FFD700', fontSize: 14, fontWeight: 700, fontFamily: "'Georgia', serif" }}>
          Suit Level Up!
        </div>
        <div style={{ color: '#c9d1d9', fontSize: 13 }}>
          You are now {suitName}
        </div>
      </div>
    </div>
  );
}
